import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { TooltipLabel } from '../ui/TooltipLabel';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { costManagementTooltips } from '../../utils/strategicFrameworkTooltips';
import { useCurrency } from '../../contexts/CurrencyContext';

/**
 * Type-specific fields that appear based on cost category selection
 */

// Semi-Variable Cost Fields
export const SemiVariableFields = ({ form, setForm }) => (
  <div className="space-y-4 border-t pt-4 mt-4">
    <h4 className="font-medium text-blue-800 flex items-center">
      Semi-Variable Cost Details
      <span className="ml-2 text-xs font-normal text-gray-500">(Fixed + Variable components)</span>
    </h4>
    
    <div className="grid grid-cols-2 gap-4">
      <div>
        <TooltipLabel htmlFor="fixed-component" tooltip={costManagementTooltips.semiVariableCosts?.fixedComponent}>
          Fixed Component ($)
        </TooltipLabel>
        <Input
          id="fixed-component"
          type="number"
          value={form.fixed_component}
          onChange={(e) => setForm({...form, fixed_component: e.target.value})}
          placeholder="e.g., 200"
        />
        <p className="text-xs text-gray-500 mt-1">Base monthly charge regardless of usage</p>
      </div>

      <div>
        <TooltipLabel htmlFor="fixed-portion" tooltip="Percentage of cost that's fixed">
          Fixed Portion (%)
        </TooltipLabel>
        <Input
          id="fixed-portion"
          type="number"
          value={form.fixed_portion}
          onChange={(e) => setForm({...form, fixed_portion: e.target.value})}
          placeholder="e.g., 40"
        />
        <p className="text-xs text-gray-500 mt-1">What % is fixed vs variable</p>
      </div>

      <div>
        <TooltipLabel htmlFor="variable-rate" tooltip={costManagementTooltips.semiVariableCosts?.variableRate}>
          Variable Rate
        </TooltipLabel>
        <Input
          id="variable-rate"
          type="number"
          value={form.variable_rate}
          onChange={(e) => setForm({...form, variable_rate: e.target.value})}
          placeholder="e.g., 0.15"
        />
        <p className="text-xs text-gray-500 mt-1">Cost per unit of variable component</p>
      </div>

      <div>
        <TooltipLabel htmlFor="variable-basis" tooltip="Unit of measurement for variable portion">
          Variable Basis
        </TooltipLabel>
        <Select value={form.variable_basis || ''} onValueChange={(val) => setForm({...form, variable_basis: val})}>
          <SelectTrigger>
            <SelectValue placeholder="Select basis" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="per_kwh">Per kWh (Electricity)</SelectItem>
            <SelectItem value="per_call">Per Call (Phone)</SelectItem>
            <SelectItem value="per_gb">Per GB (Data)</SelectItem>
            <SelectItem value="per_transaction">Per Transaction</SelectItem>
            <SelectItem value="per_mile">Per Mile (Vehicle)</SelectItem>
            <SelectItem value="per_hour">Per Hour (Usage)</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  </div>
);

// Direct Cost Fields
export const DirectCostFields = ({ form, setForm, products }) => (
  <div className="space-y-4 border-t pt-4 mt-4">
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
      <h4 className="font-medium text-blue-800 mb-2">💡 Direct Cost Configuration</h4>
      <p className="text-sm text-blue-700 mb-3">
        Direct costs are now configured through the main form fields above:
      </p>
      <ul className="text-sm text-blue-700 space-y-2 ml-4">
        <li>✓ <strong>Cost Type:</strong> Select &quot;Per Unit Produced&quot; or &quot;Percentage of Revenue&quot;</li>
        <li>✓ <strong>Link to Product:</strong> Use Strategic Framework Linkage section to link to specific products</li>
        <li>✓ <strong>Cost Calculation:</strong> System automatically calculates based on product performance</li>
      </ul>
      <div className="mt-3 pt-3 border-t border-blue-200">
        <p className="text-xs text-blue-600">
          <strong>Note:</strong> The product assignment and unit cost fields have been replaced by the dynamic calculation system. 
          Link your cost to a product via Strategic Framework section above.
        </p>
      </div>
    </div>
  </div>
);

// Indirect Cost Fields
export const IndirectCostFields = ({ form, setForm, products }) => (
  <div className="space-y-4 border-t pt-4 mt-4">
    <h4 className="font-medium text-blue-800">Indirect Cost Allocation</h4>

    <div>
      <TooltipLabel htmlFor="allocation-method" tooltip={costManagementTooltips.indirectCosts?.allocationMethod}>
        Cost Allocation Method
      </TooltipLabel>
      <Select value={form.allocation_method || ''} onValueChange={(val) => setForm({...form, allocation_method: val})}>
        <SelectTrigger>
          <SelectValue placeholder="Select allocation method" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="revenue">By Revenue % - Product with 30% revenue pays 30% of cost</SelectItem>
          <SelectItem value="units">By Units Produced - Equal cost per unit across products</SelectItem>
          <SelectItem value="headcount">By Headcount - Based on employees per product line</SelectItem>
          <SelectItem value="equal">Equal Split - Divide equally across all products</SelectItem>
          <SelectItem value="floor_space">By Floor Space - Based on manufacturing space used</SelectItem>
        </SelectContent>
      </Select>
    </div>

    <div>
      <TooltipLabel htmlFor="allocation-base" tooltip={costManagementTooltips.indirectCosts?.totalAmount}>
        Total Amount to Allocate ($)
      </TooltipLabel>
      <Input
        id="allocation-base"
        type="number"
        value={form.allocation_base}
        onChange={(e) => setForm({...form, allocation_base: e.target.value})}
        placeholder="e.g., 10000"
      />
      <p className="text-xs text-gray-500 mt-1">Total cost to be shared across products</p>
    </div>

    <div>
      <Label>Products Sharing This Cost</Label>
      <div className="max-h-32 overflow-y-auto border rounded p-2 bg-white mt-1">
        {products.length === 0 ? (
          <p className="text-sm text-gray-500 p-2">No products available</p>
        ) : (
          products.map(product => (
            <label key={product.id} className="flex items-center space-x-2 p-1 hover:bg-gray-50 cursor-pointer">
              <input 
                type="checkbox" 
                checked={form.linked_product_ids?.includes(product.id)}
                onChange={(e) => {
                  const newIds = e.target.checked 
                    ? [...(form.linked_product_ids || []), product.id]
                    : (form.linked_product_ids || []).filter(id => id !== product.id);
                  setForm({...form, linked_product_ids: newIds});
                }}
                className="rounded border-gray-300"
              />
              <span className="text-sm">{product.name}</span>
            </label>
          ))
        )}
      </div>
    </div>
  </div>
);

// OpEx Fields
export const OpExFields = ({ form, setForm }) => (
  <div className="space-y-4 border-t pt-4 mt-4">
    <h4 className="font-medium text-blue-800">Operating Expense Details</h4>

    <div className="grid grid-cols-2 gap-4">
      <div>
        <TooltipLabel htmlFor="department" tooltip="Which department owns this expense">
          Department
        </TooltipLabel>
        <Select value={form.department || ''} onValueChange={(val) => setForm({...form, department: val})}>
          <SelectTrigger>
            <SelectValue placeholder="Select department" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="admin">Administration</SelectItem>
            <SelectItem value="sales">Sales</SelectItem>
            <SelectItem value="marketing">Marketing</SelectItem>
            <SelectItem value="hr">Human Resources</SelectItem>
            <SelectItem value="it">IT</SelectItem>
            <SelectItem value="finance">Finance</SelectItem>
            <SelectItem value="operations">Operations</SelectItem>
            <SelectItem value="general">General</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <TooltipLabel htmlFor="budget-allocation" tooltip="Allocated budget for this expense">
          Budget Allocation ($)
        </TooltipLabel>
        <Input
          id="budget-allocation"
          type="number"
          value={form.budget_allocation}
          onChange={(e) => setForm({...form, budget_allocation: e.target.value})}
          placeholder="e.g., 50000"
        />
      </div>
    </div>

    <div className="flex items-center space-x-6">
      <label className="flex items-center space-x-2 cursor-pointer">
        <input 
          type="checkbox" 
          checked={form.is_recurring}
          onChange={(e) => setForm({...form, is_recurring: e.target.checked})}
          className="rounded border-gray-300"
        />
        <span className="text-sm">Recurring Expense</span>
      </label>

      <label className="flex items-center space-x-2 cursor-pointer">
        <input 
          type="checkbox" 
          checked={form.approval_required}
          onChange={(e) => setForm({...form, approval_required: e.target.checked})}
          className="rounded border-gray-300"
        />
        <span className="text-sm">Requires Approval</span>
      </label>
    </div>
  </div>
);

// CapEx Fields
export const CapExFields = ({ form, setForm, formatCurrency: formatCurrencyProp }) => {
  const { formatCurrency: formatCurrencyContext } = useCurrency();
  const formatCurrency = formatCurrencyProp || formatCurrencyContext;
  
  const calculateAnnualDepreciation = () => {
    const cost = parseFloat(form.amount) || 0;
    const salvage = parseFloat(form.salvage_value) || 0;
    const life = parseInt(form.useful_life_years) || 1;
    
    if (form.depreciation_method === 'straight_line') {
      return (cost - salvage) / life;
    }
    return 0;
  };

  return (
    <div className="space-y-4 border-t pt-4 mt-4">
      <h4 className="font-medium text-blue-800">Asset & Depreciation Details</h4>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <TooltipLabel htmlFor="asset-name" tooltip={costManagementTooltips.capex?.assetName}>
            Asset Name
          </TooltipLabel>
          <Input
            id="asset-name"
            value={form.asset_name}
            onChange={(e) => setForm({...form, asset_name: e.target.value})}
            placeholder="e.g., Production Line #1"
          />
        </div>

        <div>
          <TooltipLabel htmlFor="useful-life" tooltip={costManagementTooltips.capex?.usefulLife}>
            Useful Life (Years)
          </TooltipLabel>
          <Input
            id="useful-life"
            type="number"
            value={form.useful_life_years}
            onChange={(e) => setForm({...form, useful_life_years: e.target.value})}
            placeholder="e.g., 10"
          />
        </div>

        <div>
          <TooltipLabel htmlFor="salvage-value" tooltip={costManagementTooltips.capex?.salvageValue}>
            Salvage Value ($)
          </TooltipLabel>
          <Input
            id="salvage-value"
            type="number"
            value={form.salvage_value}
            onChange={(e) => setForm({...form, salvage_value: e.target.value})}
            placeholder="e.g., 5000"
          />
        </div>

        <div>
          <TooltipLabel htmlFor="depreciation-method" tooltip={costManagementTooltips.capex?.depreciationMethod}>
            Depreciation Method
          </TooltipLabel>
          <Select value={form.depreciation_method} onValueChange={(val) => setForm({...form, depreciation_method: val})}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="straight_line">Straight-Line (Equal each year)</SelectItem>
              <SelectItem value="declining_balance">Declining Balance (Higher early years)</SelectItem>
              <SelectItem value="units_of_production">Units of Production (Usage-based)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Depreciation Calculator */}
      {form.amount && form.useful_life_years && (
        <div className="bg-blue-50 border border-blue-200 rounded p-4">
          <h5 className="font-medium text-blue-800 mb-3">Depreciation Preview</h5>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Annual Depreciation:</span>
              <span className="font-bold text-gray-900">{formatCurrency(calculateAnnualDepreciation())}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Total Depreciation (5yr):</span>
              <span className="font-bold text-gray-900">{formatCurrency(calculateAnnualDepreciation() * 5)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Book Value (Year 5):</span>
              <span className="font-bold text-gray-900">{formatCurrency(parseFloat(form.amount) - calculateAnnualDepreciation() * 5)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Financial Cost Fields
export const FinancialCostFields = ({ form, setForm, financialLoans, formatCurrency: formatCurrencyProp }) => {
  const { formatCurrency: formatCurrencyContext } = useCurrency();
  const formatCurrency = formatCurrencyProp || formatCurrencyContext;
  const [linkType, setLinkType] = React.useState(form.loan_reference_id ? 'existing_loan' : 'standalone');
  const selectedLoan = financialLoans.find(l => l.id === form.loan_reference_id);

  React.useEffect(() => {
    if (linkType === 'standalone') {
      setForm({...form, loan_reference_id: ''});
    }
  }, [linkType]);

  return (
    <div className="space-y-4 border-t pt-4 mt-4">
      <h4 className="font-medium text-blue-800">Financial Cost Configuration</h4>

      {financialLoans.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
          <p className="text-sm text-yellow-800">
            💡 <strong>Tip:</strong> Link to existing loans from Financial Management or create standalone financial cost
          </p>
        </div>
      )}

      <div className="flex items-center space-x-4">
        <label className="flex items-center space-x-2 cursor-pointer">
          <input 
            type="radio" 
            value="existing_loan"
            checked={linkType === 'existing_loan'}
            onChange={() => setLinkType('existing_loan')}
            className="border-gray-300"
          />
          <span className="text-sm">Link to Existing Loan</span>
        </label>
        <label className="flex items-center space-x-2 cursor-pointer">
          <input 
            type="radio" 
            value="standalone"
            checked={linkType === 'standalone'}
            onChange={() => setLinkType('standalone')}
            className="border-gray-300"
          />
          <span className="text-sm">Standalone Financial Cost</span>
        </label>
      </div>

      {linkType === 'existing_loan' && financialLoans.length > 0 ? (
        <div>
          <TooltipLabel htmlFor="loan-link" tooltip="Select loan from Financial Management">
            Link to Loan
          </TooltipLabel>
          <Select value={form.loan_reference_id || ''} onValueChange={(val) => setForm({...form, loan_reference_id: val})}>
            <SelectTrigger>
              <SelectValue placeholder="Select loan" />
            </SelectTrigger>
            <SelectContent>
              {financialLoans.map(loan => (
                <SelectItem key={loan.id} value={loan.id}>
                  {loan.loan_name} - {loan.interest_rate}% ({loan.lender || 'Lender'})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedLoan && (
            <div className="bg-blue-50 p-3 rounded mt-2 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-gray-600">Principal:</span> <span className="font-medium">{formatCurrency(selectedLoan.principal || 0)}</span></div>
                <div><span className="text-gray-600">Interest Rate:</span> <span className="font-medium">{selectedLoan.interest_rate}%</span></div>
                <div><span className="text-gray-600">Monthly Payment:</span> <span className="font-medium">{formatCurrency(selectedLoan.monthly_payment || 0)}</span></div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <TooltipLabel htmlFor="loan-name" tooltip={costManagementTooltips.financialCosts?.loanName}>
              Loan/Cost Name
            </TooltipLabel>
            <Input
              id="loan-name"
              value={form.loan_name}
              onChange={(e) => setForm({...form, loan_name: e.target.value})}
              placeholder="e.g., ABC Bank Business Loan"
            />
          </div>

          <div>
            <TooltipLabel htmlFor="interest-rate" tooltip={costManagementTooltips.financialCosts?.interestRate}>
              Interest Rate (%)
            </TooltipLabel>
            <Input
              id="interest-rate"
              type="number"
              value={form.interest_rate}
              onChange={(e) => setForm({...form, interest_rate: e.target.value})}
              placeholder="e.g., 8.5"
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Tax Fields
export const TaxFields = ({ form, setForm }) => (
  <div className="space-y-4 border-t pt-4 mt-4">
    <h4 className="font-medium text-blue-800">Tax Configuration</h4>

    <div className="grid grid-cols-2 gap-4">
      <div>
        <TooltipLabel htmlFor="tax-type" tooltip={costManagementTooltips.tax?.taxType}>
          Tax Type
        </TooltipLabel>
        <Select value={form.tax_type || ''} onValueChange={(val) => setForm({...form, tax_type: val})}>
          <SelectTrigger>
            <SelectValue placeholder="Select tax type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="income">Corporate Income Tax</SelectItem>
            <SelectItem value="vat">Value Added Tax (VAT)</SelectItem>
            <SelectItem value="sales">Sales Tax</SelectItem>
            <SelectItem value="property">Property Tax</SelectItem>
            <SelectItem value="payroll">Payroll Tax (Employer)</SelectItem>
            <SelectItem value="withholding">Withholding Tax</SelectItem>
            <SelectItem value="excise">Excise Tax</SelectItem>
            <SelectItem value="import">Import Duties</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <TooltipLabel htmlFor="tax-rate" tooltip={costManagementTooltips.tax?.taxRate}>
          Tax Rate (%)
        </TooltipLabel>
        <Input
          id="tax-rate"
          type="number"
          value={form.tax_rate}
          onChange={(e) => setForm({...form, tax_rate: e.target.value})}
          placeholder="e.g., 15"
        />
      </div>
    </div>

    <div>
      <TooltipLabel htmlFor="tax-base" tooltip={costManagementTooltips.tax?.taxBase}>
        Tax Base
      </TooltipLabel>
      <Select value={form.tax_base} onValueChange={(val) => setForm({...form, tax_base: val})}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="revenue">Revenue - Tax on total sales</SelectItem>
          <SelectItem value="profit">Profit - Tax on net income</SelectItem>
          <SelectItem value="payroll">Payroll - Tax on employee wages</SelectItem>
          <SelectItem value="asset_value">Asset Value - Tax on property/assets</SelectItem>
        </SelectContent>
      </Select>
    </div>

    <label className="flex items-center space-x-2 cursor-pointer">
      <input 
        type="checkbox" 
        checked={form.is_deductible}
        onChange={(e) => setForm({...form, is_deductible: e.target.checked})}
        className="rounded border-gray-300"
      />
      <span className="text-sm">Tax Deductible - Can reduce taxable income</span>
    </label>
  </div>
);

export default {
  SemiVariableFields,
  DirectCostFields,
  IndirectCostFields,
  OpExFields,
  CapExFields,
  FinancialCostFields,
  TaxFields
};
